// import React, { useEffect, useState } from "react";
// import { DataGrid } from "@mui/x-data-grid";
// import { TextField, Typography, Box } from "@mui/material";
// import axios from "axios";

// const Searchhistories = () => {
//     const [searchText, setSearchText] = useState("");
//     const [data, setData] = useState([]);
//     const [error, setError] = useState(null);

//     // Fetch Data from API
//     const fetchSearchHistory = async () => {
//         try {
//             const res = await axios.get("https://api.medstown.com/pharmacy/medicine/top-searches");
//             setData(res?.data.reverse() || []); // Reverse for recent searches
//         } catch (error) {
//             setError("Failed to fetch data");
//             console.error(error);
//         }
//     };

//     useEffect(() => {
//         fetchSearchHistory();
//     }, []);

//     // Handle Search Input
//     const handleSearchTextChange = (event) => {
//         setSearchText(event.target.value);
//     };

//     // Filter Rows Based on Search Input
//     const filteredRows = data
//         .filter((row) =>
//             Object.values(row).some(
//                 (value) =>
//                     value &&
//                     value.toString().toLowerCase().includes(searchText.toLowerCase())
//             )
//         )
//         .map((row, index) => ({
//             ...row,
//             id: row._id || `temp-id-${index}`, // Assign `_id` or a unique fallback ID
//         }));

//     // Table Columns
//     const tableColumns = [
//         { field: "_id", headerName: "Search Term medicine", width: 300 },
//         { field: "count", headerName: "Search Count", width: 200 },
//         { 
//             field: "pincodes", 
//             headerName: "Pincodes", 
//             width: 250, 
//             valueGetter: (params) => params.row.pincodes.join(", ") 
//         },
//         { 
//             field: "searchedAt", 
//             headerName: "Created Date & Time", 
//             width: 220, 
//             valueGetter: (params) => {
//                 const date = new Date(params.row.searchedAt);
//                 return `${date.toLocaleDateString()} | ${date.toLocaleTimeString()}`;
//             }
//         }
//     ];

//     return (
//         <Box padding={3}>
//             {error && <Typography color="error">{error}</Typography>}

//             {/* Search Bar */}
//             <TextField
//                 label="Search"
//                 variant="outlined"
//                 fullWidth
//                 value={searchText}
//                 onChange={handleSearchTextChange}
//                 style={{ marginBottom: "20px" }}
//             />

//             {/* Data Grid */}
//             <Box height="700px">
//                 <DataGrid
//                     rows={filteredRows}
//                     columns={tableColumns}
//                     pageSize={5}
//                     rowsPerPageOptions={[5, 10, 20]}
//                     disableSelectionOnClick
//                     sx={{ width: "100%" }}
//                 />
//             </Box>
//         </Box>
//     );
// };

// export default Searchhistories;



import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  alpha,
  Container,
  CircularProgress,
  useMediaQuery,
  Stack,
  Paper,
  Button,
} from "@mui/material";
import { MdSearch, MdClear, MdHistory, MdDownload } from "react-icons/md";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import debounce from "lodash.debounce";

const Searchhistories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // States
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        "https://api.medstown.com/pharmacy/medicine/top-searches"
      );
      const fetchedData = (res?.data || []).slice().reverse();
      // Ensure each row has a unique 'id' prop for DataGrid
      const rowsWithId = fetchedData.map((row, idx) => ({
        ...row,
        id: row._id || `temp-${idx}`,
      }));
      setData(rowsWithId);
      setFilteredRows(rowsWithId);
    } catch (err) {
      setError("Failed to fetch data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Debounced search filtering
  const debouncedFilter = useMemo(() => {
    return debounce((value, rows) => {
      const filtered = rows.filter((row) =>
        Object.values(row).some((val) =>
          val?.toString().toLowerCase().includes(value.toLowerCase())
        )
      );
      setFilteredRows(filtered);
    }, 300);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedFilter(value, data);
  };

  const clearSearch = () => {
    setSearchText("");
    setFilteredRows(data);
  };

  // Columns for DataGrid
  const columns = [
    {
      field: "searchTerm",
      headerName: "Search Term (Medicine)",
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => params.row.searchTerm || params.row._id || "—",
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          title={params.value}
          sx={{ fontWeight: 600, color: theme.palette.primary.dark }}
        >
          {params.value}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "count",
      headerName: "Search Count",
      flex: 0.5,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {params.value ?? 0}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "pincodes",
      headerName: "Pincodes",
      flex: 1,
      minWidth: 180,
      valueGetter: (params) =>
        Array.isArray(params.row.pincodes) ? params.row.pincodes.join(", ") : "—",
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          title={params.value}
          sx={{ color: theme.palette.text.secondary }}
        >
          {params.value}
        </Typography>
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: "searchedAt",
      headerName: "Created Date & Time",
      flex: 0.7,
      minWidth: 180,
      valueGetter: (params) => {
        if (!params.row.searchedAt) return "—";
        const dt = new Date(params.row.searchedAt);
        return `${dt.toLocaleDateString()} | ${dt.toLocaleTimeString()}`;
      },
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value}
        </Typography>
      ),
      sortable: true,
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 3 } }}>
      {/* Header Section */}
      <Paper
        sx={{
          mb: 3,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: "white",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          boxShadow: "0 6px 20px rgba(1, 77, 77, 0.3)",
        }}
        elevation={4}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <MdHistory size={40} />
          <Box>
            <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800} letterSpacing={-1}>
              Search Histories
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Latest searched medicines by users
            </Typography>
          </Box>
        </Stack>

      
      </Paper>

      {/* Search Bar */}
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        value={searchText}
        onChange={handleSearchChange}
        placeholder="Search medicines, pincodes..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MdSearch color={alpha(theme.palette.primary.main, 0.7)} size={20} />
            </InputAdornment>
          ),
          endAdornment: searchText && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={clearSearch} aria-label="Clear search">
                <MdClear color={alpha(theme.palette.primary.main, 0.7)} size={20} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          fontSize: "0.85rem",
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
          },
        }}
      />

      {/* Error Message */}
      {error && (
        <Typography color="error" sx={{ textAlign: "center", mb: 2, fontWeight: 600 }}>
          {error}
        </Typography>
      )}

      {/* DataGrid */}
      <Paper
        sx={{
          height: { xs: "auto", sm: 700 },
          borderRadius: 3,
          "& .MuiDataGrid-root": {
            border: "none",
            fontSize: "0.9rem",
            backgroundColor: theme.palette.background.paper,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 700,
            fontSize: "1rem",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(theme.palette.secondary.main, 0.15),
            cursor: "pointer",
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              height: 400,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
              Loading...
            </Typography>
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ height: 400, textAlign: "center", p: 4 }}>
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              No matching search histories
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your search terms or clear the filter.
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5, 10, 20]}
            disableSelectionOnClick
            autoHeight={isMobile}
            getRowId={(row) => row.id}
            pagination
          />
        )}
      </Paper>
    </Container>
  );
};

export default Searchhistories;
